import { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Star, Camera, Check, ArrowLeft, ArrowRight, PaperPlaneTilt, MapPin, Broadcast } from "@phosphor-icons/react";
import { api, formatApiErrorDetail } from "@/lib/api";

export default function WalkRoute() {
  const { id } = useParams();
  const [route, setRoute] = useState(null);
  const [idx, setIdx] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    try { const r = await api.get(`/walk-routes/${id}`); setRoute(r.data); }
    catch (e) { toast.error("Failed to load"); }
  };
  useEffect(() => { load(); }, [id]);

  if (!route) return <div className="min-h-screen bg-slate-900 text-white flex items-center justify-center">Loading…</div>;

  const stops = route.stops || [];
  const s = stops[idx] || {};
  const totalDone = stops.filter(x => x.completed_at).length;

  const patch = async (payload) => {
    setBusy(true);
    try {
      await api.patch(`/walk-routes/${id}/stops/${s.id}`, payload);
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed"); }
    finally { setBusy(false); }
  };

  const uploadPhotos = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    for (const f of Array.from(files)) {
      const fd = new FormData(); fd.append("file", f);
      try { await api.post(`/walk-routes/${id}/stops/${s.id}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" }}); }
      catch (e) { toast.error("Upload failed"); }
    }
    setUploading(false);
    await load();
  };

  const completeStop = async () => {
    await patch({ completed: true });
    if (idx < stops.length - 1) setIdx(idx + 1);
  };

  const finishWalk = async () => {
    if (!window.confirm("Finish the walk and email the report?")) return;
    try { await api.post(`/walk-routes/${id}/complete`); toast.success("Walk complete · report sent"); await load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Complete failed"); }
  };

  return (
    <div className="min-h-screen bg-slate-900 text-white" data-testid="walk-route-page">
      <div className="bg-black px-4 py-3 flex items-center justify-between border-b border-slate-800">
        <Link to="/admin/walk-radar" data-testid="walk-back" className="text-slate-400 hover:text-white text-xs font-black uppercase tracking-widest inline-flex items-center gap-1">
          <ArrowLeft size={12}/> Walks
        </Link>
        <div className="text-center">
          <div className="text-[10px] font-black uppercase tracking-widest text-sky-400">WalkRadar™</div>
          <div className="text-xs font-bold">{route.name}</div>
        </div>
        <div className="text-[10px] font-black uppercase tracking-widest text-slate-400">
          {totalDone}/{stops.length}
        </div>
      </div>

      <div className="max-w-lg mx-auto p-4">
        <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">Stop {idx + 1} of {stops.length}</div>
        <h2 className="font-display font-black text-2xl mt-1" data-testid="walk-stop-address">
          {s.address}{s.unit ? ` · Unit ${s.unit}` : ""}
        </h2>
        {s.label && <div className="text-xs text-sky-400 mt-1">{s.label}</div>}

        {/* Ratings */}
        {["cleanliness", "exterior", "common_areas"].map(k => (
          <div key={k} className="mt-4">
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1">
              {k.replace("_"," ")}
            </div>
            <div className="flex gap-1" data-testid={`walk-rate-${k}`}>
              {[1,2,3,4,5].map(n => (
                <button key={n} onClick={() => patch({ [k]: n })} disabled={busy}
                  data-testid={`walk-rate-${k}-${n}`}
                  className="p-1">
                  <Star weight={n <= (s[k] || 0) ? "fill" : "regular"} size={30}
                    className={n <= (s[k] || 0) ? "text-amber-400" : "text-slate-600"}/>
                </button>
              ))}
            </div>
          </div>
        ))}

        {/* Toggles */}
        <div className="mt-5 grid grid-cols-2 gap-2">
          {[
            { k: "cleaning_needed", label: "🧹 Cleaning" },
            { k: "trash_needed", label: "🗑️ Trash" },
            { k: "pest_needed", label: "🐜 Pest" },
            { k: "documented", label: "📄 Documented" },
          ].map(t => (
            <button key={t.k} onClick={() => patch({ [t.k]: !s[t.k] })} disabled={busy}
              data-testid={`walk-toggle-${t.k}`}
              className={`h-11 border-2 text-xs font-black uppercase tracking-widest ${s[t.k] ? "bg-emerald-600 border-emerald-600 text-white" : "border-slate-600 text-slate-300 hover:border-white"}`}>
              {t.label}
            </button>
          ))}
        </div>

        {/* Note */}
        <textarea value={s.note || ""} onChange={e => patch({ note: e.target.value })}
          onBlur={e => patch({ note: e.target.value })} rows={3} placeholder="Notes for this stop…"
          data-testid="walk-note"
          className="mt-4 w-full px-3 py-2 bg-slate-800 border-2 border-slate-700 text-white text-sm focus:border-sky-500 focus:outline-none"/>

        {/* Photos */}
        <div className="mt-4">
          <div className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2">Photos ({(s.photos || []).length})</div>
          <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="walk-photo-btn"
            className="w-full h-11 bg-sky-600 text-white text-xs font-black uppercase tracking-widest inline-flex items-center justify-center gap-2 hover:opacity-90 disabled:opacity-40">
            <Camera size={14}/> {uploading ? "Uploading…" : "Add Photos"}
          </button>
          <input ref={fileRef} type="file" accept="image/*" multiple className="hidden"
            onChange={e => { uploadPhotos(e.target.files); e.target.value = ""; }}/>
        </div>

        {/* Nav */}
        <div className="mt-6 flex gap-2">
          <button onClick={() => setIdx(Math.max(0, idx - 1))} disabled={idx === 0}
            data-testid="walk-prev" className="flex-1 h-12 border-2 border-slate-600 text-xs font-black uppercase tracking-widest disabled:opacity-30">
            <ArrowLeft size={14} className="inline"/> Prev
          </button>
          <button onClick={completeStop} disabled={busy}
            data-testid="walk-next"
            className="flex-1 h-12 bg-emerald-600 text-xs font-black uppercase tracking-widest hover:opacity-90 disabled:opacity-40 inline-flex items-center justify-center gap-1">
            <Check size={14}/> Complete Stop
          </button>
        </div>

        {totalDone === stops.length && route.status !== "completed" && (
          <button onClick={finishWalk} data-testid="walk-finish-btn"
            className="mt-4 w-full h-14 bg-gradient-to-r from-sky-600 to-emerald-600 text-white text-xs font-black uppercase tracking-widest inline-flex items-center justify-center gap-2">
            <PaperPlaneTilt size={16} weight="fill"/> Finish & Email Report
          </button>
        )}
        {route.status === "completed" && (
          <div className="mt-4 p-4 bg-emerald-900/40 border border-emerald-700 text-emerald-200 text-xs font-black uppercase tracking-widest text-center">
            ✓ Walk Completed · Report Sent
          </div>
        )}
      </div>
    </div>
  );
}
